import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { apiJson, ApiError } from '../../lib/api'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { useLayer } from '../../components/layers'
import { PendingButton, Failed } from '../../components/pending'
import { money, plural } from '../wages/format'
import { colName, workbook, XLSX_TYPE, type CellValue } from '../batches/xlsx'
import { MAX_FILE_BYTES, toBase64 } from '../sites/ImportDialog'
import type { Person } from './PersonRecordPanel'
import o from '../../components/Overlay.module.css'
import ip from './ImportPanel.module.css'

// The Personnel import (handoff §7b "Import and export", as approved in D5:
// A-1(a) to A-7(a), C-3). The admin assigns the file's columns to our fields;
// nothing is guessed, and only the template's headings and the tenant's own
// earlier choices for exactly the same headings arrive preassigned. The
// server reads the file, plans every row (matched by employee ID, then
// email, never by name) and says what would happen before anything is
// written. A held row waits for an explicit decision; choosing a person for
// it updates that stored person. Import sends the plan's hash back with a
// run key, so a plan that went stale is refused whole and a repeated press
// applies once. Nothing here grants, moves or removes CONTROL sign-in.

export interface ImportField {
  key: string
  heading: string
  required?: boolean
}

interface FileRead {
  name: string
  sheet: string | null
  headings: string[]
  rows: string[][]
  lines: number[]
  // Number cells whose shown text the server could not reproduce
  // ([row, column, format]); sent back so a row whose ID is one says why.
  numberFormats: [number, number, string][]
}

export type Outcome = 'create' | 'update' | 'unchanged' | 'held' | 'invalid' | 'skipped'

interface Candidate {
  id: string
  name: string
  email: string | null
  employee_id: string | null
  archived: boolean
  signs_in: boolean
}

export interface PlanRow {
  row: number
  outcome: Outcome
  name?: string
  values?: Record<string, string | number | null>
  target?: Candidate
  changes?: { field: string; from: unknown; to: unknown; effective_from?: string }[]
  notes?: string[]
  hold?: { code: string; message: string; options: ('update' | 'create' | 'skip')[]; candidates: Candidate[] }
  reasons?: { field: string; message: string }[]
  decision?: { action: 'update' | 'create' | 'skip'; targetId: string | null }
}

export interface Plan {
  rows: PlanRow[]
  counts: Record<Outcome, number>
  planHash: string
  today: string
}

interface Decision {
  row: number
  action: 'update' | 'create' | 'skip'
  targetId?: string
}

interface GroupOpt {
  id: string
  name: string
  is_default: boolean
  is_archived: boolean
}

export interface ImportResult {
  counts: Record<Outcome, number>
  created: { row: number; id: string }[]
  updated: { row: number; id: string }[]
  not_imported: { row: number; outcome: Outcome }[]
  already_applied?: boolean
}

// Our fields, as the server's /read names them, for when it has not yet.
const FIELDS: ImportField[] = [
  { key: 'firstName', heading: 'First name', required: true },
  { key: 'lastName', heading: 'Last name', required: true },
  { key: 'preferredName', heading: 'Preferred name' },
  { key: 'email', heading: 'Email' },
  { key: 'employeeId', heading: 'Employee ID' },
  { key: 'phone', heading: 'Phone' },
  { key: 'hourlyRate', heading: 'Hourly rate', required: true },
]

// '' is "Choose column"; 'none' is "Not in this file", a normal answer for a
// field the import does not need.
type Choice = number | '' | 'none'

const COUNTS: [Outcome, string, (n: number) => string][] = [
  ['create', 'Will be added', (n) => plural(n, 'new person', 'new people')],
  ['update', 'Will be updated', (n) => plural(n, 'person', 'people')],
  ['unchanged', 'Already up to date', (n) => plural(n, 'row', 'rows')],
  ['held', 'Held back', (n) => plural(n, 'row', 'rows')],
  ['invalid', 'Cannot be imported', (n) => plural(n, 'row', 'rows')],
  ['skipped', 'Left out', (n) => plural(n, 'row', 'rows')],
]
const COUNT_NOTE: Record<Outcome, string> = {
  create: 'In the group chosen above',
  update: 'Matched by employee ID, then email',
  unchanged: 'Nothing to change',
  held: 'Nothing is written for them',
  invalid: 'Fix these in the file',
  skipped: 'Repeats, and rows you left out',
}

const SHOWN = 200
const err = (e: any, fallback: string) => (e?.message as string) || fallback

// A run key per plan: crypto.randomUUID where there is one.
export function runKey(): string {
  const c: any = globalThis.crypto
  if (c?.randomUUID) return c.randomUUID()
  const b = new Uint8Array(16)
  if (c?.getRandomValues) c.getRandomValues(b)
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256)
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

async function fileBytes(file: File): Promise<Uint8Array> {
  if (typeof file.arrayBuffer === 'function') return new Uint8Array(await file.arrayBuffer())
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer))
    r.onerror = () => reject(r.error)
    r.readAsArrayBuffer(file)
  })
}

export const TEMPLATE_NAME = 'people-import.xlsx'

// "Get template" (handoff 7b point 6): the people already here in the
// import's own headings, so editing it and bringing it back maps itself.
// Employee IDs are written as text, and the column is formatted as text, so
// "007" comes back as "007", and so does one typed into it later.
export function peopleTemplate(people: Person[]): Uint8Array<ArrayBuffer> {
  const rows: CellValue[][] = [FIELDS.map((f) => f.heading)]
  for (const p of people) {
    if (p.archived || p.merged_into_id) continue
    rows.push([
      p.first_name,
      p.last_name,
      p.preferred_name || '',
      p.email || '',
      p.employee_id || '',
      p.phone || '',
      p.hourly_rate == null ? null : Number(p.hourly_rate),
    ])
  }
  return workbook([{ name: 'People', rows, widths: [16, 18, 16, 28, 14, 16, 12], text: [FIELDS.findIndex((f) => f.key === 'employeeId')] }])
}

const fmt = (field: string, v: unknown) => {
  if (v === null || v === undefined || v === '') return '(blank)'
  if (field === 'hourlyRate') return money(Number(v))
  return String(v)
}
const who = (c: Candidate) =>
  `${c.name}${[c.employee_id && `ID ${c.employee_id}`, c.email].filter(Boolean).length ? ` (${[c.employee_id && `ID ${c.employee_id}`, c.email].filter(Boolean).join(', ')})` : ''}`

export function ImportPanel({
  user,
  people,
  onClose,
  onDone,
}: {
  user: any
  people: Person[]
  onClose: () => void
  onDone: (message: string) => Promise<void> | void
}) {
  const [source, setSource] = useState<'file' | 'payroll'>('file')
  const [groups, setGroups] = useState<GroupOpt[] | null>(null)
  const [groupId, setGroupId] = useState<string | null>(null)
  const [file, setFile] = useState<FileRead | null>(null)
  const [fields, setFields] = useState<ImportField[]>(FIELDS)
  const [choice, setChoice] = useState<Record<string, Choice>>({})
  const [from, setFrom] = useState<Record<string, 'saved' | 'template'>>({})
  const [decisions, setDecisions] = useState<Decision[]>([])
  const [plan, setPlan] = useState<Plan | null>(null)
  const [planning, setPlanning] = useState(false)
  const [planError, setPlanError] = useState('')
  const [key, setKey] = useState(runKey)
  const [filter, setFilter] = useState<Outcome | null>(null)
  const [reading, setReading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const { startTask } = useTasks()
  const dialogEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: !!file && !busy, el: dialogEl, modal: true, busy })

  useEffect(() => {
    let live = true
    apiJson<GroupOpt[]>(user, '/api/groups')
      .then((r) => {
        if (!live) return
        const list = (Array.isArray(r) ? r : []).filter((g) => !g.is_archived)
        setGroups(list)
        setGroupId((cur) => cur ?? list.find((g) => g.is_default)?.id ?? null)
      })
      .catch(() => live && setGroups([]))
    return () => {
      live = false
    }
  }, [user])

  // The mapping as the server takes it: field to column, or left out.
  const mapping: Record<string, number> = {}
  for (const f of fields) {
    const c = choice[f.key]
    if (typeof c === 'number') mapping[f.key] = c
  }
  const missing = fields.filter((f) => f.required && typeof choice[f.key] !== 'number')
  const complete = !!file && !missing.length
  const body = file
    ? {
        headings: file.headings,
        rows: file.rows,
        lines: file.lines,
        ...(file.numberFormats.length ? { numberFormats: file.numberFormats } : {}),
        mapping,
        groupId,
        ...(decisions.length ? { decisions } : {}),
      }
    : null
  const bodyKey = JSON.stringify(body && complete ? body : null)

  // The preview runs whenever a complete mapping, the group or a decision
  // changes. A later answer replaces an earlier one; an earlier one that
  // arrives late is dropped.
  const seq = useRef(0)
  useEffect(() => {
    if (!body || !complete) {
      setPlan(null)
      setPlanError('')
      return
    }
    const mine = ++seq.current
    setPlanning(true)
    setPlanError('')
    apiJson<Plan>(user, '/api/personnel/import/plan', { method: 'POST', body })
      .then((p) => {
        if (mine !== seq.current) return
        setPlan(p)
        setKey(runKey())
      })
      .catch((e: any) => {
        if (mine !== seq.current) return
        setPlan(null)
        // A choice the new plan no longer asks about (the person it named
        // changed) is dropped, and the rows are checked again without it.
        if (e instanceof ApiError && e.body?.code === 'DECISION_INVALID' && decisions.length) {
          setDecisions([])
          setPlanError('Someone changed since you chose for the held rows, so those choices were cleared. Choose again.')
          return
        }
        setPlanError(err(e, 'The rows could not be checked'))
      })
      .finally(() => mine === seq.current && setPlanning(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyKey])

  const template = () => startTask(fileTask('Import template', 'People', TEMPLATE_NAME, XLSX_TYPE, () => peopleTemplate(people)))

  const choose = async (f: File | undefined) => {
    setFile(null)
    setPlan(null)
    setChoice({})
    setFrom({})
    setDecisions([])
    setFilter(null)
    setError('')
    if (!f) return
    if (f.size > MAX_FILE_BYTES) {
      setError(`This file is larger than ${MAX_FILE_BYTES / 1024} KB. Import it in parts.`)
      return
    }
    setReading(true)
    try {
      const r = await apiJson<{
        sheet: string | null
        headings: string[]
        rows: string[][]
        lines: number[]
        numberFormats?: [number, number, string][]
        fields?: ImportField[]
        assigned?: Record<string, number>
        source?: Record<string, 'saved' | 'template'>
      }>(user, '/api/personnel/import/read', { method: 'POST', body: { file_name: f.name, data: toBase64(await fileBytes(f)) } })
      const fl = r.fields?.length ? r.fields : FIELDS
      // Required fields first (handoff 7b point 2), each in the server's order.
      setFields([...fl.filter((x) => x.required), ...fl.filter((x) => !x.required)])
      setChoice({ ...(r.assigned || {}) })
      setFrom({ ...(r.source || {}) })
      setFile({ name: f.name, sheet: r.sheet, headings: r.headings, rows: r.rows, lines: r.lines, numberFormats: r.numberFormats || [] })
    } catch (e: any) {
      setError(err(e, 'The file could not be read'))
    } finally {
      setReading(false)
    }
  }

  const setField = (k: string, v: string) => {
    setChoice((c) => ({ ...c, [k]: v === '' ? '' : v === 'none' ? 'none' : Number(v) }))
    setFrom((x) => {
      const n = { ...x }
      delete n[k]
      return n
    })
    setDecisions([])
  }

  const decide = (d: Decision | { row: number; action: null }) =>
    setDecisions((list) => [...list.filter((x) => x.row !== d.row), ...(d.action ? [d as Decision] : [])])

  const n = plan ? plan.counts.create + plan.counts.update : 0
  const blocked = !file
    ? reading
      ? ''
      : 'Choose a file first'
    : missing.length
      ? `Assign ${missing.map((f) => f.heading.toLowerCase()).join(', ')} first`
      : planning || !plan
        ? ''
        : !n
          ? 'Nothing in this file would add or change anyone'
          : ''

  const submit = async () => {
    if (busy || !body || !plan || blocked) return
    setBusy(true)
    setError('')
    try {
      const r = await apiJson<ImportResult>(user, '/api/personnel/import/apply', {
        method: 'POST',
        body: { ...body, runKey: key, planHash: plan.planHash },
      })
      const c = r.counts
      const parts = [`${plural(c.create, 'person', 'people')} added`, `${c.update} updated`]
      const left = r.not_imported?.length || 0
      await onDone(`${r.already_applied ? 'This import was already applied' : 'Imported'}: ${parts.join(', ')}${left ? ` · ${left} not imported` : ''}`)
    } catch (e: any) {
      // A plan that changed comes back as it now reads; nothing was written.
      if (e instanceof ApiError && e.body?.code === 'STALE_PLAN') {
        if (e.body.plan) {
          setPlan(e.body.plan)
          setKey(runKey())
        } else {
          setDecisions([])
        }
      }
      setError(err(e, 'Nothing was imported'))
    } finally {
      setBusy(false)
    }
  }

  const fieldLabel = (k: string) => fields.find((f) => f.key === k)?.heading || k
  const shown = plan ? plan.rows.filter((r) => (filter ? r.outcome === filter : r.outcome !== 'unchanged')) : []
  const defaultGroup = groups?.find((g) => g.is_default)

  return (
    <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
      <div ref={dialogEl} tabIndex={-1} className={`${o.modal} ${o.wide} ${ip.dialog}`} role="dialog" aria-label="Import people">
        <div className={`${o.head} ${ip.head}`}>
          <div className={o.headText}>
            <h2 className={o.title}>Import people</h2>
            <p className={o.sub}>Nothing is created until you press Import. Everything below can be changed first.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={ip.body}>
        <div className={ip.sources} role="radiogroup" aria-label="Where the people come from">
          <button type="button" role="radio" aria-checked={source === 'file'} className={`${ip.source} ${source === 'file' ? ip.sourceOn : ''}`} onClick={() => setSource('file')}>
            <span className={ip.sourceIcon}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg></span>
            <span className={ip.sourceText}>
              <span className={ip.sourceName}>CSV or Excel</span>
              <span className={ip.sourceNote}>A file from the contractor or your own list</span>
            </span>
          </button>
          <button type="button" role="radio" aria-checked={source === 'payroll'} className={`${ip.source} ${source === 'payroll' ? ip.sourceOn : ''}`} onClick={() => setSource('payroll')}>
            <span className={ip.sourceIcon}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0zM12 18v4" /></svg></span>
            <span className={ip.sourceText}>
              <span className={ip.sourceName}>Payroll (PayHero, Xero)</span>
              <span className={ip.sourceNote}>Employees are pulled from a connected payroll under Integrations.</span>
            </span>
          </button>
        </div>

        {source === 'payroll' ? (
          <div className={ip.payroll} data-testid="import-payroll">
            <span>
              A payroll pull names its own fields, so there is nothing to assign. It runs from the payroll’s connection, which shows the organisation, the employees
              available and the last pull, and it never creates a duplicate silently.
            </span>
            <Link to="/integrations">Open Integrations</Link>
          </div>
        ) : (
          <>
            <p className={ip.lead}>
              Rows are matched to people already here by employee ID, then email, never by name. A row that only shares a name with someone is held for you to decide.
              New rates take effect today. An import never gives anyone CONTROL sign-in.
            </p>

            <div className={ip.block}>
              <div className={ip.blockHead}>
                <label className={o.label} htmlFor="imp-group">
                  Assign to group
                </label>
                <span className={ip.blockNote}>New people join this group. People already here keep theirs.</span>
              </div>
              <div className={ip.groupLine}>
              <select id="imp-group" className={`${o.input} ${ip.groupSelect}`} value={groupId ?? ''} disabled={!groups || busy} onChange={(e) => setGroupId(e.target.value || null)}>
                {!groups && <option value="">Loading…</option>}
                {/* A tenant's default group is made the first time someone needs
                    it, so until then "no group" is that default, offered by name. */}
                {groups && !defaultGroup && <option value="">Direct employees (Default)</option>}
                {(groups || []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                    {g.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
              {groups && (groupId === null || defaultGroup?.id === groupId) && <span className={ip.badgeDefault}>Default</span>}
              </div>
            </div>

            <div className={ip.fileRow}>
              <span className={ip.fileIcon}><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></svg></span>
              <div className={ip.fileText}>
                <label className={ip.fileTitle} htmlFor="imp-people-file">
                  Choose a CSV or Excel file
                </label>
                <input
                  id="imp-people-file"
                  type="file"
                  className={ip.fileInput}
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  disabled={busy || reading}
                  onChange={(e) => choose(e.target.files?.[0])}
                />
                {reading && <span className={ip.fileName}>Reading…</span>}
              </div>
              <button type="button" className={ip.smallGhost} onClick={template} disabled={busy}>
                Get template
              </button>
            </div>

            {file && (
              <>
                <div className={ip.fileName} data-testid="import-file">
                  {file.name}
                  {file.sheet ? `, sheet ${file.sheet}` : ''}: {plural(file.rows.length, 'row', 'rows')} under {plural(file.headings.length, 'heading', 'headings')}
                </div>
                {file.sheet !== null && (
                  <p className={ip.lead}>Employee IDs are read as Excel shows them where the file says how: 42 formatted as 0000 reads 0042. A plain number keeps no zeros the file does not hold, so format that column as text in Excel to keep them.</p>
                )}

                <div className={ip.block}>
                  <div className={ip.blockHead}>
                    <span className={o.label}>Assign columns</span>
                    <span className={ip.blockNote}>Pick which column in your file holds each field. Our template arrives pre-assigned.</span>
                  </div>
                  <ul className={ip.fields} data-testid="import-mapping">
                    {fields.map((f) => {
                      const c = choice[f.key] ?? ''
                      const done = typeof c === 'number'
                      const needed = f.required && !done
                      const usedBy = (col: number) => fields.find((o) => o.key !== f.key && choice[o.key] === col)
                      return (
                        <li key={f.key} className={`${ip.field} ${done ? ip.fieldDone : needed ? ip.fieldNeeded : ''}`} data-testid={`map-${f.key}`}>
                          <label htmlFor={`map-${f.key}`} className={ip.fieldName}>
                            {f.heading}
                            {f.required && <span className={ip.fieldReq}> · required</span>}
                            {done && from[f.key] && <span className={ip.fieldFrom}>{from[f.key] === 'saved' ? 'As you assigned it last time' : 'Our template’s heading'}</span>}
                            {!done && <span className={ip.fieldFrom}>{f.required ? 'Pick the column holding this' : 'Pick a column, or leave out'}</span>}
                          </label>
                          <svg className={ip.fieldArrow} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                            <path d="m9 18 6-6-6-6" />
                          </svg>
                          <select id={`map-${f.key}`} className={c === '' ? `${ip.select} ${ip.unset}` : ip.select} value={String(c)} disabled={busy} onChange={(e) => setField(f.key, e.target.value)}>
                            <option value="">Choose column</option>
                            {!f.required && <option value="none">Not in this file</option>}
                            {file.headings.map((h, i) => {
                              const other = usedBy(i)
                              return (
                                <option key={i} value={i} disabled={!!other}>
                                  {h || `Column ${colName(i)} (no heading)`}
                                  {other ? ` — used for ${other.heading}` : ''}
                                </option>
                              )
                            })}
                          </select>
                          <span className={`${ip.badge} ${done ? ip.badgeDone : needed ? ip.badgeNeeded : ''}`}>{done ? 'Done' : needed ? 'Needed' : c === 'none' ? 'Not in file' : 'Optional'}</span>
                        </li>
                      )
                    })}
                  </ul>
                  {missing.length ? (
                    <span className={ip.waiting} data-testid="import-waiting">
                      Assign {missing.map((f) => f.heading.toLowerCase()).join(', ')} and we’ll check all {plural(file.rows.length, 'row', 'rows')}.
                    </span>
                  ) : (
                    <span className={ip.ready}>All three required fields are mapped.{planning ? ` Checking ${plural(file.rows.length, 'row', 'rows')}…` : ''}</span>
                  )}
                </div>
              </>
            )}

            {planError && <div className={o.warn}>{planError}</div>}

            {plan && complete && (
              <>
                <div className={ip.counts} data-testid="import-counts">
                  {COUNTS.map(([o, head, main]) => (
                    <button
                      key={o}
                      type="button"
                      className={`${ip.count} ${o === 'held' && plan.counts.held ? ip.countHeld : ''} ${filter === o ? ip.countOn : ''}`}
                      aria-pressed={filter === o}
                      disabled={!plan.counts[o]}
                      onClick={() => setFilter((x) => (x === o ? null : o))}
                    >
                      <span className={ip.countHead}>{head}</span>
                      <span className={ip.countMain}>{main(plan.counts[o])}</span>
                      <span className={ip.countNote}>{COUNT_NOTE[o]}</span>
                    </button>
                  ))}
                </div>

                <div className={ip.block}>
                  <div className={`${ip.blockHead} ${ip.rowsHead}`}>
                    {filter ? COUNTS.find((x) => x[0] === filter)![1] : 'What each row will do'}
                    {filter ? (
                      <>
                        {' · '}
                        <button type="button" className={ip.linkBtn} onClick={() => setFilter(null)}>
                          Show every row
                        </button>
                      </>
                    ) : plan.counts.unchanged ? (
                      <span className={ip.blockNote}> (rows already up to date are under their count)</span>
                    ) : null}
                  </div>
                  <ul className={ip.rows} data-testid="import-rows">
                    {shown.slice(0, SHOWN).map((r) => (
                      <RowItem key={r.row} r={r} fieldLabel={fieldLabel} busy={busy || planning} decide={decide} />
                    ))}
                  </ul>
                  {shown.length > SHOWN && <p className={ip.lead}>and {shown.length - SHOWN} more</p>}
                </div>
              </>
            )}
          </>
        )}

        </div>

        <div className={`${o.foot} ${ip.foot}`}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            {source === 'file' ? 'Cancel' : 'Close'}
          </button>
          {source === 'file' && blocked && <span className={o.footNote}>{blocked}</span>}
          {source === 'file' && (
            <PendingButton
              className={o.primary}
              busy={busy}
              pending="Importing…"
              disabled={!!blocked || !plan || planning}
              onClick={submit}
            >
              {plan && complete && n ? `Import ${plural(n, 'person', 'people')}` : 'Import'}
            </PendingButton>
          )}
          <Failed text={error} />
        </div>
      </div>
      {confirm}
    </div>
  )
}

const OUTCOME_TEXT: Record<Outcome, string> = {
  create: 'New person',
  update: 'Update',
  unchanged: 'Already up to date',
  held: 'Held back',
  invalid: 'Cannot be imported',
  skipped: 'Left out',
}

function RowItem({
  r,
  fieldLabel,
  busy,
  decide,
}: {
  r: PlanRow
  fieldLabel: (k: string) => string
  busy: boolean
  decide: (d: Decision | { row: number; action: null }) => void
}) {
  const held = r.outcome === 'held'
  const [target, setTarget] = useState('')
  const opts = r.hold?.options || []
  const live = (r.hold?.candidates || []).filter((c) => !c.archived)
  return (
    <li className={`${ip.rowItem} ${held || r.outcome === 'invalid' ? ip.rowHeld : ''}`} data-testid={`import-row-${r.row}`}>
      <div className={ip.rowHead}>
        <span className={ip.rowWhere}>Row {r.row}</span>
        {r.name && <span>{r.name}</span>}
        <span className={ip.rowWhat}>
          · {OUTCOME_TEXT[r.outcome]}
          {r.outcome === 'update' || r.outcome === 'unchanged' ? ` ${r.target?.name || ''}` : ''}
        </span>
      </div>
      {r.outcome === 'create' && r.values && (
        <ul className={ip.rowDetail}>
          <li>Hourly rate {fmt('hourlyRate', r.values.hourlyRate)} from today</li>
          {r.values.employeeId && <li>Employee ID {String(r.values.employeeId)}</li>}
          {r.values.email && <li>{String(r.values.email)}</li>}
        </ul>
      )}
      {r.changes && r.changes.length > 0 && (
        <ul className={ip.rowDetail}>
          {r.changes.map((c) => (
            <li key={c.field}>
              {fieldLabel(c.field)}: {fmt(c.field, c.from)} → {fmt(c.field, c.to)}
              {c.effective_from ? ' from today' : ''}
            </li>
          ))}
        </ul>
      )}
      {r.hold && <span>{r.hold.message}</span>}
      {r.reasons && (
        <ul className={ip.rowDetail}>
          {r.reasons.map((x, i) => (
            <li key={i}>{x.message}</li>
          ))}
        </ul>
      )}
      {r.notes && r.notes.length > 0 && (
        <ul className={ip.rowDetail}>
          {r.notes.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      )}

      {held && opts.some((o) => o !== 'skip') && (
        <div className={ip.choices}>
          {opts.includes('update') &&
            (r.hold?.candidates || []).map((c) => (
              <label key={c.id} className={ip.choice}>
                <input type="radio" name={`target-${r.row}`} value={c.id} disabled={busy || c.archived} checked={target === c.id} onChange={() => setTarget(c.id)} />
                <span>
                  {c.archived ? `${who(c)}: archived, restore them first to update them` : `This is ${who(c)}`}
                </span>
              </label>
            ))}
          <div className={ip.rowActions}>
            {opts.includes('update') && live.length > 0 && (
              <button
                type="button"
                className={ip.smallGhost}
                disabled={busy || !target}
                onClick={() => decide({ row: r.row, action: 'update', targetId: target })}
              >
                Update the person chosen
              </button>
            )}
            {opts.includes('create') && (
              <button type="button" className={ip.smallGhost} disabled={busy} onClick={() => decide({ row: r.row, action: 'create' })}>
                Add as a new person
              </button>
            )}
            <button type="button" className={ip.smallGhost} disabled={busy} onClick={() => decide({ row: r.row, action: 'skip' })}>
              Leave out
            </button>
          </div>
        </div>
      )}
      {r.decision && (
        <div className={ip.rowActions}>
          <span>
            Your choice:{' '}
            {r.decision.action === 'skip' ? 'leave this row out' : r.decision.action === 'create' ? 'a new person' : `update ${r.target?.name || 'the person chosen'}`}
          </span>
          <button type="button" className={ip.linkBtn} disabled={busy} onClick={() => decide({ row: r.row, action: null })}>
            Undo choice
          </button>
        </div>
      )}
      {!r.decision && (r.outcome === 'create' || r.outcome === 'update') && (
        <div className={ip.rowActions}>
          <button type="button" className={ip.linkBtn} disabled={busy} onClick={() => decide({ row: r.row, action: 'skip' })}>
            Leave this row out
          </button>
        </div>
      )}
    </li>
  )
}
